import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import Modal from '../../../components/UI/Modal';
import Button from '../../../components/UI/Button';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import { phoneNumberHealthService } from '../../../services/phoneNumberHealthService';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPurchased?: () => void;
  defaultAreaCode?: string;
}

const PurchaseNumberModal: React.FC<Props> = ({ isOpen, onClose, onPurchased, defaultAreaCode = '' }) => {
  const [areaCode, setAreaCode] = useState(defaultAreaCode);

  useEffect(() => {
    if (isOpen && defaultAreaCode) setAreaCode(defaultAreaCode);
  }, [isOpen, defaultAreaCode]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [results, setResults] = useState<Array<{ phoneNumber: string; region?: string }>>([]);

  const handleSearch = async () => {
    const ac = areaCode.replace(/\D/g, '').slice(0, 3);
    if (ac.length !== 3) {
      toast.error('Enter a 3-digit area code');
      return;
    }
    setSearching(true);
    try {
      const numbers = await phoneNumberHealthService.searchAvailable(ac);
      const list = Array.isArray(numbers) ? numbers : numbers?.numbers ?? [];
      setResults(list);
      if (list.length === 0) toast('No numbers available for this area code');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (phoneNumber: string) => {
    setPurchasing(phoneNumber);
    try {
      await phoneNumberHealthService.purchaseNumber(phoneNumber);
      toast.success(`${phoneNumber} purchased successfully`);
      onPurchased?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Purchase Phone Number" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Search Telnyx inventory by area code, then purchase a number. New numbers start in warm-up status after sync.
        </p>
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-[140px]">
            <input
              type="text"
              inputMode="numeric"
              maxLength={3}
              placeholder="e.g. 305"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <Button onClick={handleSearch} loading={searching} disabled={areaCode.length < 3}>
            <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
            Search
          </Button>
        </div>

        {searching && (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        )}

        {!searching && results.length > 0 && (
          <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
            {results.map((n) => {
              const num = n.phoneNumber ?? (n as any).phone_number ?? '';
              return (
                <div key={num} className="flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/30">
                  <div>
                    <p className="font-mono font-medium">{num}</p>
                    {n.region && <p className="text-xs text-muted-foreground">{n.region}</p>}
                  </div>
                  <Button
                    size="sm"
                    loading={purchasing === num}
                    disabled={!!purchasing}
                    onClick={() => handlePurchase(num)}
                  >
                    <ShoppingCartIcon className="h-4 w-4 mr-1" />
                    Purchase
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {!searching && results.length === 0 && areaCode.length === 3 && (
          <p className="text-sm text-muted-foreground text-center py-6">Search to see available numbers.</p>
        )}
      </div>
    </Modal>
  );
};

export default PurchaseNumberModal;
